import { Box, Col, FormGroup, FormParagraph, Row, Text } from '@ksyos/design-system';
import type { ValidationErrors } from '@frontend/utils';
import { calculateBmi } from '@common/utils/bmi';
import { concatOrNull } from '@common/utils/textUtils';
import { NumberInputContext } from './processActivityFormGroupFields';

interface BmiFormGroupProps {
    errors: ValidationErrors;
    fieldKey: {
        bmi?: string;
        height: string;
        weight: string;
    };
    formGroupLabel?: string;
    hasBmiInput?: boolean;
    height: number | null;
    isRequired: boolean;
    weight: number | null;
}

export function BmiFormGroup(props: BmiFormGroupProps): JSX.Element {
    const { hasBmiInput, fieldKey, formGroupLabel, errors, height, weight, isRequired } = props;

    const hasWeightError = errors.has(fieldKey.weight);
    const hasHeightError = errors.has(fieldKey.height);

    return (
        <FormGroup
            hasErrors={hasWeightError || hasHeightError}
            isRequired={isRequired}
            label={formGroupLabel || 'BMI'}
        >
            <Box
                display="grid"
                gridTemplateColumns="max-content max-content max-content"
                sx={{ columnGap: 8 }}
            >
                <Box
                    display="grid"
                    gridTemplateColumns="80px max-content"
                >
                    <NumberInputContext
                        error={hasWeightError ? 'weight' : undefined}
                        errorArgs={{ max: '750', min: '0' }}
                        fieldKey={fieldKey.weight}
                        maximumFractionDigits={2}
                    />
                    <Text
                        fontWeight="medium"
                        ml={3}
                        mt={2}
                        size="sm"
                    >
                        kg
                    </Text>
                </Box>
                <Box
                    display="grid"
                    gridTemplateColumns="80px max-content"
                >
                    <NumberInputContext
                        error={hasHeightError ? 'height' : undefined}
                        errorArgs={{ max: '300', min: '0' }}
                        fieldKey={fieldKey.height}
                    />
                    <Text
                        fontWeight="medium"
                        ml={3}
                        mt={2}
                        size="sm"
                    >
                        cm
                    </Text>
                </Box>
                {hasBmiInput ? (
                    <Box
                        display="grid"
                        gridTemplateColumns="80px max-content"
                    >
                        <NumberInputContext
                            error={fieldKey.bmi && errors.has(fieldKey.bmi) ? 'bmi' : undefined}
                            errorArgs={{ max: '150', min: '0' }}
                            fieldKey={fieldKey.bmi}
                            maximumFractionDigits={2}
                        />
                        <Text
                            fontWeight="medium"
                            ml={3}
                            mt={2}
                            size="sm"
                        >
                            BMI
                        </Text>
                    </Box>
                ) : (
                    <Text
                        color="minimal"
                        fontWeight="medium"
                        mt={2}
                        size="sm"
                    >
                        {weight && height ? concatOrNull(calculateBmi(weight, height), 'kg/m2') : 'BMI'}
                    </Text>
                )}
            </Box>
        </FormGroup>
    );
}

export function BmiFormGroupFormParagraph(props: Pick<BmiFormGroupProps, 'height' | 'weight'>): JSX.Element | null {
    const { height, weight } = props;

    if (!height && !weight) {
        return null;
    }

    return (
        <FormGroup label="Gewicht / lengte / BMI">
            <Row columnGap={8}>
                <Col>
                    <FormParagraph>{concatOrNull(weight, 'kg')}</FormParagraph>
                </Col>
                <Col>
                    <FormParagraph>{concatOrNull(height, 'cm')}</FormParagraph>
                </Col>
                <Col>
                    <FormParagraph>{concatOrNull(calculateBmi(weight, height), 'kg/m2')}</FormParagraph>
                </Col>
            </Row>
        </FormGroup>
    );
}
